import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import {
  FindingsScreen,
  type FindingsSearch,
} from '@/components/discovery/findings/findings-screen';
import { ReviewRedirectNotice } from '@/components/engagement/review-redirect-notice';

/**
 * Discovery -> Findings (T-0188): one area at a time, or the whole
 * evidence ledger. The address holds the view so it can be shared:
 *
 *   ?area=<area id | all>   the area on the right, or All evidence
 *   &status=<status>        the ledger's status filter
 *   &q=<search>             search all evidence (opens the ledger)
 *   &evidence=<id>          an item to bring into view in its area
 *   &document=<id>          a document whose proposed passages are open for review (T-0257)
 *   &from=proposals         landed here from the IV&V review route, which
 *                           an engagement with no IV&V module redirects;
 *                           a one-line notice says so until the next move
 */

const text = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

type FindingsRouteSearch = FindingsSearch & { from?: 'proposals' };

export const Route = createFileRoute('/engagements/$engagementId/discovery/findings')({
  validateSearch: (search: Record<string, unknown>): FindingsRouteSearch => {
    const out: FindingsRouteSearch = {};
    const area = text(search.area);
    const status = text(search.status);
    const q = text(search.q);
    const evidence = text(search.evidence);
    const document = text(search.document);
    if (area) out.area = area;
    if (status) out.status = status;
    if (q) out.q = q;
    if (evidence) out.evidence = evidence;
    if (document) out.document = document;
    if (search.from === 'proposals') out.from = 'proposals';
    return out;
  },
  component: FindingsRoute,
});

function FindingsRoute() {
  const { engagementId } = Route.useParams();
  const { from, ...search } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onSearchChange = useCallback(
    (next: FindingsSearch, opts?: { replace?: boolean }) => {
      void navigate({
        search: {
          ...(next.area ? { area: next.area } : {}),
          ...(next.status ? { status: next.status } : {}),
          ...(next.q ? { q: next.q } : {}),
          ...(next.evidence ? { evidence: next.evidence } : {}),
          ...(next.document ? { document: next.document } : {}),
        },
        replace: opts?.replace,
      });
    },
    [navigate],
  );
  return (
    <>
      {from === 'proposals' ? (
        <div className="px-6 pt-4">
          <ReviewRedirectNotice target="discovery" />
        </div>
      ) : null}
      <FindingsScreen engagementId={engagementId} search={search} onSearchChange={onSearchChange} />
    </>
  );
}
